import { motion } from 'motion/react';
import { formatDistanceToNow } from 'date-fns';
import { User } from 'lucide-react';
import SourceCitations from '../Research/SourceCitations';
import type { Message } from '@/types';
import { PERSONAS } from '@/types';

interface MessageBubbleProps {
  message: Message;
  index: number;
}

const MessageBubble = ({ message, index }: MessageBubbleProps) => {
  const isUser = message.role === 'user';
  const persona = message.persona ? PERSONAS.find((p) => p.id === message.persona) : null;
  const time = message.timestamp ? formatDistanceToNow(message.timestamp, { addSuffix: true }) : '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05, ease: 'easeOut' }}
      className={`flex items-start gap-2.5 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      {!isUser && (
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm shadow-sm mt-1"
          style={{ backgroundColor: persona ? `${persona.color}20` : '#E2E8F0' }}
        >
          {persona ? persona.icon : '🤖'}
        </div>
      )}

      <div className={`flex flex-col gap-1 max-w-[80%] min-w-0 ${isUser ? 'items-end' : 'items-start'}`}>
        <div
          className={`px-[18px] py-[14px] text-[15px] leading-relaxed shadow-sm ${
            isUser ? 'message-user' : 'message-ai'
          }`}
          style={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            maxWidth: '100%',
            minWidth: '0',
          }}
        >
          {message.content}
        </div>

        {message.sources && message.sources.length > 0 && (
          <div className="w-full">
            <SourceCitations sources={message.sources} />
          </div>
        )}

        <span className="text-[11px] text-slate-400 dark:text-slate-500 px-1">
          {time}
        </span>
      </div>

      {isUser && (
        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 mt-1">
          <User className="w-4 h-4 text-slate-600 dark:text-slate-300" />
        </div>
      )}
    </motion.div>
  );
};

export default MessageBubble;
